import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import NomirioLogo from "@/components/NomirioLogo";
import NotificationBell from "@/components/NotificationBell";

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isDashboard = location.pathname.startsWith("/dashboard") || location.pathname.startsWith("/admin");
  const { user, profile, signOut } = useAuth();

  if (isDashboard) return null;

  const isBuyer = profile?.role === "buyer";
  const navLinks = isBuyer
    ? [{ to: "/browse", label: "Browse Requests" }]
    : [{ to: "/browse", label: "Browse Requests" }, { to: "/pricing", label: "Pricing" }];

  return (
    <motion.nav
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50 glass"
      aria-label="Primary"
      role="navigation"
    >
      <div className="container mx-auto flex h-16 items-center justify-between px-4">
        <Link to="/" className="flex h-full items-center">
          <NomirioLogo size="header" priority className="shrink-0" />
        </Link>

        <div className="hidden md:flex items-center gap-6">
          {navLinks.map((link) => (
            <Link key={link.to} to={link.to} className="relative text-sm text-muted-foreground hover:text-foreground transition-colors group">
              {link.label}
              <motion.span
                className="absolute -bottom-1 left-0 h-0.5 bg-primary"
                initial={{ width: 0 }}
                whileHover={{ width: "100%" }}
                transition={{ duration: 0.3 }}
              />
            </Link>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          {user && profile ? (
            <>
              <NotificationBell />
              <Link to={`/dashboard/${profile.role}`}>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">Dashboard</Button>
                </motion.div>
              </Link>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground" onClick={() => signOut()}>
                  Sign Out
                </Button>
              </motion.div>
            </>
          ) : (
            <>
              <Link to="/login">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">Log In</Button>
                </motion.div>
              </Link>
              <Link to="/signup">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">Get Started</Button>
                </motion.div>
              </Link>
            </>
          )}
        </div>

        <div className="md:hidden flex items-center gap-1">
          {user && profile && <NotificationBell />}
          <button className="text-foreground p-2 -mr-2" onClick={() => setMobileOpen(!mobileOpen)}>
            <AnimatePresence mode="wait">
              <motion.div key={mobileOpen ? "close" : "menu"} initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
                {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </motion.div>
            </AnimatePresence>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden surface-2 border-t border-border p-4 space-y-3 overflow-hidden"
          >
            <Link to="/browse" className="block text-sm text-muted-foreground" onClick={() => setMobileOpen(false)}>Browse Requests</Link>
            {!isBuyer && (
              <Link to="/pricing" className="block text-sm text-muted-foreground" onClick={() => setMobileOpen(false)}>Pricing</Link>
            )}
            <div className="flex gap-2 pt-2">
              {user && profile ? (
                <>
                  <Link to={`/dashboard/${profile.role}`}><Button variant="ghost" size="sm">Dashboard</Button></Link>
                  <Button variant="ghost" size="sm" onClick={() => { signOut(); setMobileOpen(false); }}>Sign Out</Button>
                </>
              ) : (
                <>
                  <Link to="/login"><Button variant="ghost" size="sm">Log In</Button></Link>
                  <Link to="/signup"><Button size="sm" className="bg-primary text-primary-foreground">Get Started</Button></Link>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
